import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Quote, Building2, TrendingUp } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseStudiesPage = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies?published_only=true`);
      setCaseStudies(response.data);
    } catch (error) {
      console.error("Failed to fetch case studies:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="case-studies-page">
      <SEOHead 
        title="Case Studies"
        description="Real results from strategic advisory engagements. See how we've helped experts and operators build leverage and enterprise value."
        url="https://tessaauthority.com/case-studies"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12" data-testid="case-studies-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Client Results
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Proven<br />
              <span className="text-primary">Transformations</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              See how we've helped established experts and operators transform their businesses 
              into structured, scalable, and transferable assets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-12 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="case-studies-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Loading case studies...</p>
            </div>
          ) : caseStudies.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20 bg-card/5 border border-white/5"
            >
              <Building2 className="h-12 w-12 text-primary mx-auto mb-6" strokeWidth={1} />
              <h3 className="font-serif text-2xl text-foreground mb-4">Case Studies Coming Soon</h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                We're currently documenting our client transformations. 
                Check back soon for detailed case studies.
              </p>
              <Link to="/contact">
                <Button className="bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs font-bold px-8 py-5 rounded-none">
                  Start Your Transformation
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="space-y-16">
              {caseStudies.map((study, index) => (
                <motion.article
                  key={study.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border border-white/5 overflow-hidden"
                  data-testid={`case-study-${index}`}
                >
                  <div className="p-8 md:p-12">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-xs font-medium tracking-[0.15em] uppercase text-primary">
                        {study.industry}
                      </span>
                    </div>
                    
                    <h2 className="font-serif text-3xl md:text-4xl font-normal text-foreground mb-4">
                      {study.title}
                    </h2>
                    
                    <p className="text-sm text-muted-foreground mb-6">
                      Client: {study.client_name}
                    </p>

                      <div className="space-y-6 mb-8">
                        <div>
                          <h4 className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                            Challenge
                          </h4>
                          <p className="text-base font-light text-muted-foreground">
                            {study.challenge}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                            Solution
                          </h4>
                          <p className="text-base font-light text-muted-foreground">
                            {study.solution}
                          </p>
                        </div>
                      </div>

                      {/* Results */}
                      <div className="bg-card/5 border border-white/5 p-6 mb-8">
                        <h4 className="text-xs font-medium tracking-[0.15em] uppercase text-primary mb-4 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Results
                        </h4>
                        <ul className="space-y-2">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                              <span className="text-primary">•</span>
                              {result}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Testimonial */}
                      {study.testimonial && (
                        <div className="border-l-2 border-primary/30 pl-6">
                          <Quote className="h-5 w-5 text-primary mb-3" />
                          <p className="font-serif text-lg italic text-foreground mb-3">
                            "{study.testimonial}"
                          </p>
                          {study.testimonial_author && (
                            <p className="text-sm text-muted-foreground">
                              — {study.testimonial_author}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="case-studies-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Your Transformation Awaits
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-8">
              Ready to Build<br />
              <span className="text-primary">Enterprise Value?</span>
            </h2>
            <Link to="/contact">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="case-studies-cta-button"
              >
                Start Strategic Advisory
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudiesPage;
